import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import createAssetImage from 'src/assets/images/create-asset.svg';
import joinAsset from 'src/assets/images/join-asset.svg';
import { ButtonImageCard } from 'src/Components/Shared/ButtonImageCard';
import { FullScreenFormWrapper } from 'src/Containers/FullScreenFormWrapper';
import { useCreateAsset } from 'src/Hooks/Mutations/useCreateAsset';
import { useFetchMyPendingInvitations } from 'src/Hooks/Queries/useFetchMyPendingInvitations';
import { useFirstLoginDemoPopup } from 'src/Hooks/Shared/useFirstLoginDemoPopup';
import { useWelcomeVideoModal } from 'src/Hooks/Shared/useWelcomeVideoModal';
import { usePendingInvitationsModal } from 'src/Providers/PendingInvitationsModalProvider/usePendingInvitationsModal';
import { AssetDetailsDtoTypeEnum } from 'src/Services/API/generated';

import styles from './AcceptInvitationOrCreateAssetPage.module.scss';

// t('onboarding.choice.join.badge_one', '{{count}} invitation en attente')
// t('onboarding.choice.join.badge_other', '{{count}} invitations en attente')
// t('onboarding.choice.join.button.label_one', 'Voir mon invitation')
// t('onboarding.choice.join.button.label_other', 'Voir mes invitations')

const AcceptInvitationOrCreateAssetPage = () => {
  const { t } = useTranslation();

  const handleSuccessAndOpenVideo = useWelcomeVideoModal();
  useFirstLoginDemoPopup();

  const { data: pendingInvitations, isFetching: isPendingInvitationsLoading } =
    useFetchMyPendingInvitations();
  const { open: openPendingInvitationsModal } = usePendingInvitationsModal();
  const navigate = useNavigate();
  const { mutate: createAsset, isLoading: isCreateAssetLoading } = useCreateAsset({
    onSuccess: () => {
      navigate('/');
      handleSuccessAndOpenVideo();
    },
  });

  const hasPendingInvitations = pendingInvitations && pendingInvitations.length > 0;
  const invitationCount = pendingInvitations?.length ?? 0;

  const handleAcceptInvitation = () => {
    openPendingInvitationsModal({
      onAccept: (data: { asset: { id: string } }) => {
        handleSuccessAndOpenVideo(() => navigate(`/asset/${data.asset.id}`));
      },
    });
  };

  return (
    <FullScreenFormWrapper maxWidth={1100}>
      <div className={styles.title}>
        {t('onboarding.choice.title', 'Bienvenue ! Comment souhaitez-vous commencer ?')}
      </div>
      <div className={styles.cards}>
        <ButtonImageCard
          title={
            hasPendingInvitations
              ? t('onboarding.choice.join.title', "J'ai reçu une invitation")
              : t('onboarding.choice.join.titleNoInvit', 'Rejoindre un bien existant')
          }
          text={
            hasPendingInvitations
              ? t(
                  'onboarding.choice.join.text',
                  "Accédez à l'espace partagé pour commencer à co-gérer le bien."
                )
              : t(
                  'onboarding.choice.join.textNoInvit',
                  "Rejoignez un bien avec un code d'invitation ou une invitation par email."
                )
          }
          buttonLabel={
            hasPendingInvitations
              ? t('onboarding.choice.join.button.label', { count: invitationCount })
              : t('onboarding.choice.join.button.noInvit', "J'ai un code ou une invitation")
          }
          image={joinAsset}
          loading={isPendingInvitationsLoading}
          badgeText={
            hasPendingInvitations
              ? t('onboarding.choice.join.badge', { count: invitationCount })
              : undefined
          }
          onClick={() => handleAcceptInvitation()}
        />
        <ButtonImageCard
          title={t('onboarding.choice.create.title', 'Créer un nouveau bien')}
          text={t(
            'onboarding.choice.create.text',
            'Créez un espace pour votre bien et invitez vos proches, amis ou co-propriétaires à vous rejoindre.'
          )}
          buttonLabel={t('onboarding.choice.create.buttonLabel', 'Créer mon premier bien')}
          image={createAssetImage}
          onClick={() =>
            createAsset({ name: 'Mon premier bien', type: AssetDetailsDtoTypeEnum.RealEstate })
          }
          loading={isCreateAssetLoading}
        />
      </div>
    </FullScreenFormWrapper>
  );
};

export default AcceptInvitationOrCreateAssetPage;
